import { action, computed, makeObservable, observable } from 'mobx';

import type { TraceSpan } from './trace-viewer.types';

/**
 * MobX store for managing trace viewer state
 * Handles span selection, expansion, and search functionality
 */
export class TraceViewerStore {
  selectedSpanId: string | null = null;
  expandedSpanIds = new Set<string>();
  searchQuery = '';
  spanIdsMatchingSearch = new Set<string>();
  private rootSpan: TraceSpan | null = null;

  constructor() {
    makeObservable(this, {
      selectedSpanId: observable,
      expandedSpanIds: observable,
      searchQuery: observable,
      spanIdsMatchingSearch: observable,
      selectedSpan: computed,
      hasSearchResults: computed,
      selectSpan: action,
      toggleExpanded: action,
      expandAll: action,
      collapseAll: action,
      setSearchQuery: action,
      clearSearch: action,
      setRootSpan: action,
      reset: action,
    });
  }

  get selectedSpan(): TraceSpan | null {
    if (!this.selectedSpanId || !this.rootSpan) return null;
    return this.findSpanById(this.rootSpan, this.selectedSpanId);
  }

  get hasSearchResults(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  selectSpan(spanId: string | null) {
    this.selectedSpanId = spanId;
  }

  toggleExpanded(spanId: string) {
    if (this.expandedSpanIds.has(spanId)) {
      this.expandedSpanIds.delete(spanId);
    } else {
      this.expandedSpanIds.add(spanId);
    }
  }

  expandAll() {
    if (!this.rootSpan) return;
    const allIds = this.getAllSpanIds(this.rootSpan);
    this.expandedSpanIds = new Set(allIds);
  }

  collapseAll() {
    this.expandedSpanIds.clear();
  }

  /**
   * Find all ancestor span IDs for a given span ID
   * Traverses from root to find the path to the target span
   */
  private getAncestorIds(targetId: string): string[] {
    if (!this.rootSpan) return [];

    const findPath = (span: TraceSpan, path: string[]): string[] | null => {
      if (span.id === targetId) {
        return path; // Return ancestors (not including the target itself)
      }

      for (const child of span.children) {
        const result = findPath(child, [...path, span.id]);
        if (result) return result;
      }

      return null;
    };

    return findPath(this.rootSpan, []) || [];
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.spanIdsMatchingSearch.clear();

    if (query.trim() && this.rootSpan) {
      const matchingIds = this.getMatchingSpanIds(this.rootSpan, query);

      matchingIds.forEach((id) => {
        this.spanIdsMatchingSearch.add(id);
        const ancestorIds = this.getAncestorIds(id);
        ancestorIds.forEach((ancestorId) => {
          this.expandedSpanIds.add(ancestorId);
        });
      });
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.spanIdsMatchingSearch.clear();
  }

  setRootSpan(span: TraceSpan | null) {
    this.rootSpan = span;

    // Auto-expand root level
    if (span) {
      this.expandedSpanIds.add(span.id);
    }
  }

  reset() {
    this.selectedSpanId = null;
    this.expandedSpanIds.clear();
    this.searchQuery = '';
    this.spanIdsMatchingSearch.clear();
    this.rootSpan = null;
  }

  private findSpanById(span: TraceSpan, id: string): TraceSpan | null {
    if (span.id === id) return span;

    for (const child of span.children) {
      const found = this.findSpanById(child, id);
      if (found) return found;
    }

    return null;
  }

  private getAllSpanIds(span: TraceSpan): string[] {
    const ids = [span.id];
    span.children.forEach((child) => {
      ids.push(...this.getAllSpanIds(child));
    });
    return ids;
  }

  private getMatchingSpanIds(span: TraceSpan, query: string): string[] {
    const lowerQuery = query.toLowerCase();
    const matchingIds: string[] = [];

    const checkSpan = (s: TraceSpan) => {
      if (s.operationName.toLowerCase().includes(lowerQuery)) {
        matchingIds.push(s.id);
      }

      const hasMatchingTag = s.tags.some(
        (tag) =>
          tag.key.toLowerCase().includes(lowerQuery) ||
          String(tag.value).toLowerCase().includes(lowerQuery)
      );

      if (hasMatchingTag) {
        matchingIds.push(s.id);
      }

      s.children.forEach(checkSpan);
    };

    checkSpan(span);
    return matchingIds;
  }

  /**
   * Check if a span or any of its children match the search query
   */
  spanMatchesSearch(span: TraceSpan): boolean {
    if (!this.hasSearchResults) return true;

    const lowerQuery = this.searchQuery.toLowerCase();

    const matchesThisSpan =
      span.operationName.toLowerCase().includes(lowerQuery) ||
      span.tags.some(
        (tag) =>
          tag.key.toLowerCase().includes(lowerQuery) ||
          String(tag.value).toLowerCase().includes(lowerQuery)
      );

    if (matchesThisSpan) return true;

    return span.children.some((child) => this.spanMatchesSearch(child));
  }

  isSpanMatchingSearch(span: TraceSpan): boolean {
    return this.spanIdsMatchingSearch.has(span.id);
  }
}

export const traceViewerStore = new TraceViewerStore();
